/**
 * HissenPartnership - Strategic partnership section with Shanghai Hissen Engineering
 * Redesigned as a premium entity joint venture announcement
 */
import React from 'react';
import { FaBolt, FaLeaf, FaHandshake, FaGlobeAsia, FaCheck } from 'react-icons/fa';
import AnimatedSection from '../ui/AnimatedSection';

const partnershipHighlights = [
  { icon: FaBolt, label: 'Cold Ironing Systems (Ashore Power)', color: 'var(--orange)' },
  { icon: FaLeaf, label: 'Net Zero & Decarbonized Ports', color: 'var(--orange)' },
  { icon: FaHandshake, label: 'Strategic Technology Transfer', color: 'var(--orange)' },
  { icon: FaGlobeAsia, label: 'International Marine Electrical Standards', color: 'var(--orange)' }
];

const HissenPartnership = () => (
  <section style={{
    padding: '4.5rem 0',
    background: '#FFFFFF',
    position: 'relative',
    overflow: 'hidden',
    borderBottom: '1px solid rgba(10,17,40,0.05)'
  }}>
    {/* Minimalist Grid Pattern Background */}
    <div style={{
      position: 'absolute', inset: 0, opacity: 0.015,
      backgroundImage: `linear-gradient(rgba(10,17,40,1) 1px, transparent 1px), linear-gradient(90deg, rgba(10,17,40,1) 1px, transparent 1px)`,
      backgroundSize: '80px 80px', pointerEvents: 'none'
    }} />

    <div className="container-xxl px-4 px-lg-5" style={{ position: 'relative', zIndex: 1 }}>
      <div className="row align-items-center g-5">

        {/* Left Column: Partnership Details */}
        <div className="col-lg-6">
          <AnimatedSection variant="fadeLeft">
            <span className="section-tag" style={{ background: 'var(--orange-dim)', color: 'var(--orange)', borderColor: 'var(--orange-border)' }}>
              Strategic Alliance
            </span>
            <h2 className="section-title mt-2" style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 800,
              fontSize: 'clamp(1.8rem, 4vw, 2.4rem)',
              color: 'var(--navy)',
              letterSpacing: '-0.02em',
              lineHeight: 1.15
            }}>
              Business Agency of<br />
              <span style={{ color: 'var(--teal)' }}>
                Shanghai Hissen Engineering Co., Ltd.
              </span>
            </h2>
            <div className="section-divider" style={{ width: '40px', background: 'var(--orange)', height: '1px', margin: '1.5rem 0' }} />
          </AnimatedSection>

          <AnimatedSection variant="fadeLeft" delay={0.1}>
            <p style={{
              color: 'var(--text-body)',
              fontSize: '0.95rem',
              lineHeight: 1.8,
              marginBottom: '1.5rem'
            }}>
              ISAAC Group is proud to be the Business Agency representative of <strong style={{ color: 'var(--navy)' }}>Shanghai Hissen Engineering Co., Ltd.</strong> — a globally recognized leader in Marine Electrical and Green Energy Technologies. Together, we are leading the sustainability transition in Indian ports.
            </p>
            <p style={{
              color: 'var(--text-body)',
              fontSize: '0.95rem',
              lineHeight: 1.8,
              marginBottom: '2rem'
            }}>
              Through this strategic partnership, we offer turnkey solutions including high-voltage shore power systems (cold ironing), VFD drive energy optimization, smart grid integration, and comprehensive marine electrical systems designed to meet international standards.
            </p>
          </AnimatedSection>

          <AnimatedSection variant="fadeLeft" delay={0.2}>
            <div className="row g-3">
              {partnershipHighlights.map((item, i) => {
                const IconComp = item.icon;
                return (
                  <div key={i} className="col-sm-6">
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      background: 'var(--bg-page)',
                      border: '1px solid rgba(10,17,40,0.04)',
                      borderRadius: 2,
                      padding: '0.85rem 1rem'
                    }}>
                      <div style={{
                        width: '26px',
                        height: '26px',
                        borderRadius: 2,
                        background: 'var(--orange-dim)',
                        border: '1px solid var(--orange-border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <IconComp size={11} color="var(--orange)" />
                      </div>
                      <span style={{
                        fontFamily: 'var(--font-body)',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: 'var(--navy)',
                        lineHeight: 1.3
                      }}>
                        {item.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </AnimatedSection>
        </div>

        {/* Right Column: Spec Sheet/Blueprint Panel (Corporate & Editorial) */}
        <div className="col-lg-6">
          <AnimatedSection variant="zoomIn" delay={0.15}>
            <div style={{ position: 'relative' }}>
              {/* Outer frame */}
              <div style={{
                position: 'absolute',
                top: '-15px',
                left: '-15px',
                right: '15px',
                bottom: '15px',
                border: '1px solid var(--orange)',
                zIndex: 1,
                pointerEvents: 'none'
              }} />

              {/* Main Technical specs card */}
              <div style={{
                background: 'var(--navy)',
                borderRadius: 2,
                padding: '3rem 2.5rem',
                boxShadow: 'var(--shadow-lg)',
                border: '1px solid rgba(255,255,255,0.06)',
                position: 'relative',
                zIndex: 2
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <span style={{
                    color: 'var(--orange)',
                    fontSize: '0.62rem',
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 700,
                    letterSpacing: '2.5px',
                    textTransform: 'uppercase'
                  }}>Strategic Spec Sheet</span>
                  <span style={{
                    color: 'rgba(255,255,255,0.3)',
                    fontSize: '0.62rem',
                    fontFamily: 'var(--font-body)'
                  }}>Ref: HISSEN-IN-2026</span>
                </div>

                <h3 style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  fontSize: '1.6rem',
                  color: '#FFFFFF',
                  marginBottom: '0.5rem',
                  letterSpacing: '-0.01em'
                }}>
                  HISSEN MARINE
                </h3>
                <p style={{
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: '0.72rem',
                  marginBottom: '2.5rem',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  fontWeight: 600
                }}>
                  Decarbonization Infrastructure
                </p>

                {/* Specs rows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: '2rem' }}>
                  {[
                    'Ashore High-Voltage Shore Connection (HVSC)',
                    'Fuel Electric & VFD Thermal Integration',
                    'Low-Carbon Port Grid Electrification',
                    'Smart Power Distribution & Engineering Control'
                  ].map((spec, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.9rem 0',
                      borderTop: '1px solid rgba(255,255,255,0.08)',
                      fontSize: '0.8rem',
                      color: 'rgba(255,255,255,0.75)'
                    }}>
                      <FaCheck size={9} color="var(--orange)" style={{ flexShrink: 0 }} />
                      <span>{spec}</span>
                    </div>
                  ))}
                </div>

                {/* Branding Signature */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingTop: '1.5rem',
                  borderTop: '1px solid rgba(255,255,255,0.08)'
                }}>
                  <span style={{
                    fontSize: '0.55rem',
                    color: 'rgba(255,255,255,0.3)',
                    letterSpacing: '1px',
                    textTransform: 'uppercase'
                  }}>Joint Operational Representative</span>
                  <span style={{
                    background: 'var(--orange-dim)',
                    border: '1px solid var(--orange-border)',
                    color: 'var(--orange)',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    padding: '0.2rem 0.6rem',
                    borderRadius: 2,
                    fontFamily: 'var(--font-heading)',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    Business Agency
                  </span>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>

      </div>
    </div>
  </section>
);

export default HissenPartnership;
